import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  CompensationSummaryErpComparisonDetail,
  CompensationSummaryParams,
  CompensationSummaryResponse,
  MakeupTeachingRequestCreatePayload,
  MakeupTeachingRequestCreationContext,
  MakeupTeachingRequestListParams,
  MakeupTeachingRequestListResponse,
  MakeupTeachingRequestModel,
  MakeupTeachingRequestReviewListParams,
  MakeupTeachingRequestReviewListResponse,
  MakeupTeachingRequestReviewModel,
  MakeupTeachingRequestReportListParams,
  MakeupTeachingRequestReportListResponse,
} from '../../models/teach-compensation/teach-compensation.model';

@Injectable({
  providedIn: 'root',
})
export class TeachCompensationService {
  private apiUrl = '/makeup_teaching_requests';
  private reviewApiUrl = '/makeup_teaching_request_reviews';
  private reportApiUrl = '/makeup_teaching_request_reports';
  private compensationSummaryApiUrl = '/compensation_summary';

  #http = inject(HttpClient);

  getMakeupTeachingRequestsList(
    params?: MakeupTeachingRequestListParams,
  ): Observable<MakeupTeachingRequestListResponse> {
    return this.#http.get<unknown>(this.apiUrl, {
      params: this.buildListParams(params),
    }).pipe(
      map((res) => this.normalizeListResponse(res, params)),
    );
  }

  getMakeupTeachingRequestCreationContext(
    academicTermId: number,
  ): Observable<MakeupTeachingRequestCreationContext> {
    return this.#http.get<MakeupTeachingRequestCreationContext>(
      `${this.apiUrl}/creation_context`,
      { params: new HttpParams().set('academic_term_id', String(academicTermId)) },
    );
  }

  createMakeupTeachingRequest(
    payload: MakeupTeachingRequestCreatePayload,
  ): Observable<MakeupTeachingRequestModel> {
    return this.#http.post<MakeupTeachingRequestModel>(this.apiUrl, payload);
  }

  getMakeupTeachingRequest(id: number): Observable<MakeupTeachingRequestModel> {
    return this.#http.get<MakeupTeachingRequestModel>(`${this.apiUrl}/${id}`);
  }

  getMakeupTeachingRequestReviewsList(
    params?: MakeupTeachingRequestReviewListParams,
  ): Observable<MakeupTeachingRequestReviewListResponse> {
    return this.#http.get<unknown>(this.reviewApiUrl, {
      params: this.buildReviewListParams(params),
    }).pipe(
      map((res) => this.normalizeReviewListResponse(res, params)),
    );
  }

  getMakeupTeachingRequestReportList(
    params?: MakeupTeachingRequestReportListParams,
  ): Observable<MakeupTeachingRequestReportListResponse> {
    return this.#http.get<unknown>(this.reportApiUrl, {
      params: this.buildReportListParams(params),
    }).pipe(
      map((res) => this.normalizeReportListResponse(res, params)),
    );
  }

  downloadMakeupTeachingRequestReportPdf(
    params?: MakeupTeachingRequestReportListParams,
  ): Observable<Blob> {
    return this.#http.get(`${this.reportApiUrl}/pdf`, {
      params: this.buildReportListParams(params),
      responseType: 'blob',
    });
  }

  getMakeupTeachingRequestReportPdfUrl(
    params?: MakeupTeachingRequestReportListParams,
  ): string {
    const queryString = this.buildReportListParams(params).toString();
    const url = `${environment.apiUrl}${this.reportApiUrl}/pdf`;

    return queryString === '' ? url : `${url}?${queryString}`;
  }

  getCompensationSummary(
    params?: CompensationSummaryParams,
  ): Observable<CompensationSummaryResponse> {
    return this.#http.get<CompensationSummaryResponse>(this.compensationSummaryApiUrl, {
      params: this.buildCompensationSummaryParams(params),
    });
  }

  downloadRegularCompensationCalculationPdf(
    params?: CompensationSummaryParams,
  ): Observable<Blob> {
    return this.#http.get(`${this.compensationSummaryApiUrl}/pdf`, {
      params: this.buildCompensationSummaryParams(params),
      responseType: 'blob',
    });
  }

  downloadInternationalCompensationCalculationPdf(
    params?: CompensationSummaryParams,
  ): Observable<Blob> {
    return this.#http.get(`${this.compensationSummaryApiUrl}/international_pdf`, {
      params: this.buildCompensationSummaryParams(params),
      responseType: 'blob',
    });
  }

  getCompensationSummaryErpComparison(
    params?: CompensationSummaryParams,
  ): Observable<CompensationSummaryErpComparisonDetail> {
    return this.#http.get<CompensationSummaryErpComparisonDetail>(
      `${this.compensationSummaryApiUrl}/erp_comparison`,
      {
        params: this.buildCompensationSummaryParams(params),
      },
    );
  }

  approveMakeupTeachingRequestReview(id: number): Observable<MakeupTeachingRequestReviewModel> {
    return this.#http.post<MakeupTeachingRequestReviewModel>(
      `${this.reviewApiUrl}/${id}/approve`,
      {},
    );
  }

  rejectMakeupTeachingRequestReview(
    id: number,
    comment: string,
  ): Observable<MakeupTeachingRequestReviewModel> {
    return this.#http.post<MakeupTeachingRequestReviewModel>(
      `${this.reviewApiUrl}/${id}/reject`,
      { comment },
    );
  }

  cancelMakeupTeachingRequestReview(
    id: number,
    comment: string,
  ): Observable<MakeupTeachingRequestReviewModel> {
    return this.#http.post<MakeupTeachingRequestReviewModel>(
      `${this.reviewApiUrl}/${id}/cancel`,
      { comment },
    );
  }

  private buildListParams(params?: MakeupTeachingRequestListParams): HttpParams {
    let httpParams = new HttpParams();

    const supportedParams: (keyof MakeupTeachingRequestListParams)[] = [
      'schedule_submission_id',
      'makeup_teaching_leave_type_id',
      'academic_holiday_id',
      'makeup_teaching_leave_period_id',
      'subject_id',
      'subject_schedule_group_id',
      'status',
      'academic_term_id',
      'academic_term_name',
      'academic_term_year',
      'page',
      'limit',
      'sort',
      'direction',
    ];

    supportedParams.forEach((key) => {
      const value = params?.[key];
      if (value != null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });

    return httpParams;
  }

  private normalizeListResponse(
    res: unknown,
    params?: MakeupTeachingRequestListParams,
  ): MakeupTeachingRequestListResponse {
    if (res !== null && typeof res === 'object' && !Array.isArray(res)) {
      const response = res as Record<string, unknown>;

      if (Array.isArray(response.data)) {
        return {
          data: response.data as MakeupTeachingRequestModel[],
          page: Number(response.page ?? params?.page ?? 1),
          limit: Number(response.limit ?? params?.limit ?? 10),
          total: Number(response.total ?? response.data.length),
          total_pages: Number(response.total_pages ?? response.total_page ?? 1),
        };
      }
    }

    const data = Array.isArray(res) ? res as MakeupTeachingRequestModel[] : [];
    const limit = params?.limit ?? 10;

    return {
      data,
      page: params?.page ?? 1,
      limit,
      total: data.length,
      total_pages: Math.max(1, Math.ceil(data.length / limit)),
    };
  }

  private buildReviewListParams(params?: MakeupTeachingRequestReviewListParams): HttpParams {
    let httpParams = new HttpParams();

    const supportedParams: (keyof MakeupTeachingRequestReviewListParams)[] = [
      'academic_term_id',
      'academic_term_name',
      'academic_term_year',
      'status',
      'keywords',
      'department_id',
      'page',
      'limit',
      'sort',
      'direction',
    ];

    supportedParams.forEach((key) => {
      const value = params?.[key];
      if (value != null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });

    return httpParams;
  }

  private buildReportListParams(params?: MakeupTeachingRequestReportListParams): HttpParams {
    let httpParams = new HttpParams();

    const supportedParams: (keyof MakeupTeachingRequestReportListParams)[] = [
      'schedule_submission_id',
      'academic_term_name',
      'academic_term_year',
      'status',
      'month',
      'page',
      'limit',
      'sort',
      'direction',
    ];

    supportedParams.forEach((key) => {
      const value = params?.[key];
      if (value != null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });

    return httpParams;
  }

  private buildCompensationSummaryParams(params?: CompensationSummaryParams): HttpParams {
    let httpParams = new HttpParams();

    const supportedParams: (keyof CompensationSummaryParams)[] = [
      'academic_term_id',
      'academic_term_name',
      'academic_term_year',
      'certification_status',
      'page',
      'limit',
      'sort',
      'direction',
    ];

    supportedParams.forEach((key) => {
      const value = params?.[key];
      if (value != null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });

    return httpParams;
  }

  private normalizeReviewListResponse(
    res: unknown,
    params?: MakeupTeachingRequestReviewListParams,
  ): MakeupTeachingRequestReviewListResponse {
    if (res !== null && typeof res === 'object' && !Array.isArray(res)) {
      const response = res as Record<string, unknown>;

      if (Array.isArray(response.data)) {
        return {
          data: response.data as MakeupTeachingRequestReviewModel[],
          page: Number(response.page ?? params?.page ?? 1),
          limit: Number(response.limit ?? params?.limit ?? 10),
          total: Number(response.total ?? response.data.length),
          total_pages: Number(response.total_pages ?? response.total_page ?? 1),
        };
      }
    }

    const data = Array.isArray(res) ? res as MakeupTeachingRequestReviewModel[] : [];
    const limit = params?.limit ?? 10;

    return {
      data,
      page: params?.page ?? 1,
      limit,
      total: data.length,
      total_pages: Math.max(1, Math.ceil(data.length / limit)),
    };
  }

  private normalizeReportListResponse(
    res: unknown,
    params?: MakeupTeachingRequestReportListParams,
  ): MakeupTeachingRequestReportListResponse {
    if (res !== null && typeof res === 'object' && !Array.isArray(res)) {
      const response = res as Record<string, unknown>;

      if (Array.isArray(response.data)) {
        return {
          data: response.data as MakeupTeachingRequestReviewModel[],
          page: Number(response.page ?? params?.page ?? 1),
          limit: Number(response.limit ?? params?.limit ?? 10),
          total: Number(response.total ?? response.data.length),
          total_pages: Number(response.total_pages ?? response.total_page ?? 1),
          schedule_submission: response.schedule_submission as MakeupTeachingRequestReportListResponse['schedule_submission'],
          teacher: response.teacher as MakeupTeachingRequestReportListResponse['teacher'],
          department: response.department as MakeupTeachingRequestReportListResponse['department'],
          academic_rank: response.academic_rank as MakeupTeachingRequestReportListResponse['academic_rank'],
        };
      }
    }

    const data = Array.isArray(res) ? res as MakeupTeachingRequestReviewModel[] : [];
    const limit = params?.limit ?? 10;

    return {
      data,
      page: params?.page ?? 1,
      limit,
      total: data.length,
      total_pages: Math.max(1, Math.ceil(data.length / limit)),
    };
  }
}
